
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { 
  Activity, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Users,
  Zap,
  TrendingUp,
  ArrowRight
} from 'lucide-react'
import { tenantApi } from '../utils/api'
import { useSwarms } from '../hooks/useSwarms'
import { useSwarmStore } from '../stores/swarm'
import { formatDistanceToNow } from 'date-fns'
import type { Swarm } from '../types'


export default function Dashboard() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['tenant-stats'],
    queryFn: async () => {
      const response = await tenantApi.getStats({ days: 30 })
      return response.data
    },
  })

  const { data: swarmsData, isLoading: swarmsLoading } = useSwarms({ page: 1 })
  const { getActiveSwarms } = useSwarmStore()

  const activeSwarms = getActiveSwarms()
  const recentSwarms: any[] = swarmsData?.swarms?.slice(0, 5) || []

  if (statsLoading || swarmsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Overview of your agent swarms and activity
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Swarms"
          value={stats?.totalSwarms || 0}
          icon={Users}
          trend="+12%"
          trendUp={true}
        />
        <StatCard
          title="Active Now"
          value={activeSwarms.length}
          icon={Activity}
          color="blue"
        />
        <StatCard
          title="Completed"
          value={stats?.completedSwarms || 0}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          title="Failed"
          value={stats?.failedSwarms || 0}
          icon={XCircle}
          color="red"
        />
      </div>

      {/* Active Swarms */}
      {activeSwarms.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Activity className="h-5 w-5 mr-2 text-amber-500" />
              Active Swarms ({activeSwarms.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-200">
            {activeSwarms.map((swarm) => (
              <ActiveSwarmItem key={swarm.id} swarm={swarm} />
            ))}
          </div>
        </div>
      )}

      {/* Recent Swarms */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Recent Swarms</h2>
          <Link
            to="/swarms"
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
          >
            View all
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
        <div className="divide-y divide-gray-200">
          {recentSwarms.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              <p>No swarms yet.</p>
              <Link
                to="/swarms/create"
                className="mt-2 inline-flex items-center text-primary-600 hover:text-primary-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Create your first swarm
              </Link>
            </div>
          ) : (
            recentSwarms.map((swarm) => (
              <SwarmListItem key={swarm.id} swarm={swarm} />
            ))
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickActionCard
          title="Create New Swarm"
          description="Launch a new agent swarm for your task"
          icon={Zap}
          href="/swarms/create"
          color="primary"
        />
        <QuickActionCard
          title="View Analytics"
          description="Check detailed usage and performance"
          icon={TrendingUp}
          href="/swarms"
          color="accent"
        />
        <QuickActionCard
          title="API Documentation"
          description="Integrate with our REST API"
          icon={Activity}
          href="#"
          color="gray"
        />
      </div>
    </div>
  )
}

function StatCard({ 
  title, 
  value, 
  icon: Icon, 
  trend,
  trendUp,
  color = 'gray'
}: {
  title: string
  value: number
  icon: React.ElementType
  trend?: string
  trendUp?: boolean
  color?: 'gray' | 'blue' | 'green' | 'red'
}) {
  const colorClasses = {
    gray: 'bg-gray-50 text-gray-600',
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <div className="flex items-baseline">
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            {trend && (
              <span className={`ml-2 text-sm ${trendUp ? 'text-green-600' : 'text-red-600'}`}>
                {trend}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function ActiveSwarmItem({ swarm }: { swarm: Swarm }) {
  const progress = swarm.progress.totalAgents > 0
    ? Math.round((swarm.progress.completedAgents / swarm.progress.totalAgents) * 100)
    : 0

  return (
    <div className="px-6 py-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <div className="relative">
            <div className="h-2.5 w-2.5 bg-amber-500 rounded-full animate-pulse" />
            <div className="absolute inset-0 h-2.5 w-2.5 bg-amber-500 rounded-full animate-pulse-ring" />
          </div>
          <Link to={`/swarms/${swarm.id}`} className="ml-3 font-medium text-gray-900 hover:text-primary-600">
            {swarm.name}
          </Link>
        </div>
        <span className="text-xs text-gray-500">
          {formatDistanceToNow(new Date(swarm.createdAt))} ago
        </span>
      </div>
      <div className="flex items-center text-sm text-gray-500 mb-2">
        <span className="capitalize">{swarm.status}</span>
        <span className="mx-2">•</span>
        <span>Phase: {swarm.progress.currentPhase}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className="bg-amber-500 h-2 rounded-full transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="mt-1 text-xs text-gray-500">
        {swarm.progress.completedAgents} / {swarm.progress.totalAgents} agents complete
      </div>
    </div>
  )
}

function SwarmListItem({ swarm }: { swarm: any }) {
  const statusColors: Record<string, string> = {
    idle: 'bg-gray-100 text-gray-700',
    planning: 'bg-blue-100 text-blue-700',
    executing: 'bg-amber-100 text-amber-700',
    synthesizing: 'bg-purple-100 text-purple-700',
    complete: 'bg-green-100 text-green-700',
    error: 'bg-red-100 text-red-700',
  }

  return (
    <div className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center">
          <Link to={`/swarms/${swarm.id}`} className="font-medium text-gray-900 hover:text-primary-600 truncate">
            {swarm.name}
          </Link>
          <span className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[swarm.status]}`}>
            {swarm.status}
          </span>
        </div>
        <p className="mt-1 text-sm text-gray-500 truncate">{swarm.task}</p>
      </div>
      <div className="ml-4 flex items-center text-sm text-gray-500">
        <Clock className="h-4 w-4 mr-1" />
        {formatDistanceToNow(new Date(swarm.createdAt))} ago
      </div>
    </div>
  )
}

function QuickActionCard({
  title,
  description,
  icon: Icon,
  href,
  color,
}: {
  title: string
  description: string
  icon: React.ElementType
  href: string
  color: 'primary' | 'accent' | 'gray'
}) {
  const colorClasses = {
    primary: 'bg-primary-50 border-primary-200 hover:border-primary-300',
    accent: 'bg-accent-50 border-accent-200 hover:border-accent-300',
    gray: 'bg-gray-50 border-gray-200 hover:border-gray-300',
  }

  const iconColors = {
    primary: 'text-primary-600',
    accent: 'text-accent-600',
    gray: 'text-gray-600',
  }

  return (
    <Link
      to={href}
      className={`block p-6 rounded-lg border ${colorClasses[color]} transition-colors`}
    >
      <div className="flex items-start">
        <div className={`p-2 rounded-lg bg-white ${iconColors[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="ml-4">
          <h3 className="font-medium text-gray-900">{title}</h3>
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        </div>
      </div>
    </Link>
  )
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  )
}
